import { useEffect, useRef, useState } from "react";
import TaxPayer, {
  TAX_PAYER_TYPE_CHOICES,
  TaxPayerSpecialPermissions,
  UsageBlock,
} from "../../models/tax-payer";
import { generatePath, Link, useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import { backupTaxPayer, restoreTaxPayer } from "../../api/backup";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CTooltip,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
  CBadge,
  CSelect,
  CProgress,
} from "@coreui/react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import TaxPayerUpdateModal from "./TaxPayerUpdateModal";
import TaxPayerLimitsModal from "./TaxPayerLimitsModal";
import TaxPayerFixedLimitsModal from "./TaxPayerFixedLimitsModal";
import TaxPayerExtraGrantsModal from "./TaxPayerExtraGrantsModal";
import TaxPayerSpecialPermissionsModal from "./TaxPayerSpecialPermissionsModal";
import TaxPayerSuspensionModal from "./TaxPayerSuspensionModal";

const ITEMS_PER_PAGE = 20;

const getUsageColor = (percentage: number) => {
  if (percentage >= 90) return "danger";
  if (percentage >= 70) return "warning";
  return "success";
};

const renderUsageBar = (
  block: UsageBlock,
  label?: string,
  spaced?: boolean,
) => {
  return (
    <div
      className={`d-flex align-items-center${spaced ? " mt-1" : ""}`}
      style={{ gap: "4px" }}
    >
      {label && (
        <small className="text-muted" style={{ minWidth: "26px" }}>
          {label}
        </small>
      )}
      <CProgress
        className="flex-grow-1 mb-0"
        size="sm"
        color={getUsageColor(block.percentage)}
        value={Math.min(block.percentage, 100)}
      />
      <small style={{ minWidth: "34px" }}>{block.percentage}%</small>
    </div>
  );
};

const TaxPayerCrud = () => {
  const fields = [
    {
      key: "ruc",
      _classes: ["text-center"],
      label: "RUC",
    },
    { key: "socialName", _classes: ["text-center"], label: "Razón Social" },
    {
      key: "type",
      _classes: ["text-center"],
      label: "Tipo",
    },
    {
      key: "isDemo",
      _classes: ["text-center"],
      label: "Demo",
    },
    {
      key: "suspension",
      _classes: ["text-center"],
      label: "Estado",
      filter: false,
    },
    {
      key: "usage",
      _classes: ["text-center"],
      _style: { minWidth: "170px" },
      label: "% Consumo",
      filter: false,
    },
    {
      key: "createdAt",
      _classes: ["text-center"],
      label: "Fecha de Creación",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TaxPayer[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [isDemoFilter, setIsDemoFilter] = useState<string>("");

  const [showTaxPayerEditModal, setShowTaxPayerEditModal] = useState(false);
  const [taxPayer, setTaxPayer] = useState<TaxPayer | undefined>(undefined);
  const [taxPayerForPermissions, setTaxPayerForPermissions] = useState<TaxPayer>();
  const [taxPayerForSuspension, setTaxPayerForSuspension] =
    useState<TaxPayer>();

  const [showTaxPayerLimitsModal, setShowTaxPayerLimitsModal] = useState(false);
  const [taxPayerForLimits, setTaxPayerForLimits] = useState<
    TaxPayer | undefined
  >(undefined);

  const [showTaxPayerFixedLimitsModal, setShowTaxPayerFixedLimitsModal] =
    useState(false);
  const [taxPayerForFixedLimits, setTaxPayerForFixedLimits] = useState<
    TaxPayer | undefined
  >(undefined);

  const [showTaxPayerExtraGrantsModal, setShowTaxPayerExtraGrantsModal] =
    useState(false);
  const [taxPayerForExtraGrants, setTaxPayerForExtraGrants] = useState<
    TaxPayer | undefined
  >(undefined);

  const [backingUpId, setBackingUpId] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = useRef<HTMLInputElement>(null);

  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    const search = urlParams.get("search") ? urlParams.get("search") : "";
    const isDemo = urlParams.get("is_demo") ? urlParams.get("is_demo") : "";

    let map = new Map();
    map.set("search", search);
    map.set("is_demo", isDemo);
    map.set("page", page);
    return map;
  };

  const fetchItems = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const itemsRequestStatus = await getList<TaxPayer>(
      "/tax_payers/all/",
      limit,
      offset,
      urlParams,
    );

    if (itemsRequestStatus.status === SUCCESS) {
      if (itemsRequestStatus.data !== undefined) {
        const count = itemsRequestStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setItems(itemsRequestStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = itemsRequestStatus.detail
        ? itemsRequestStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }

    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : "",
    );
    urlParams.set(
      "is_demo",
      previousParams.get("is_demo") ? previousParams.get("is_demo") : "",
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    urlParams.set("is_demo", isDemoFilter);
    urlParams.set("page", "1");
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onIsDemoFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDemoFilter(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const onAddClick = () => {
    setTaxPayer(undefined);
    setShowTaxPayerEditModal(true);
  };

  const onUpdateClick = (taxPayer: TaxPayer) => {
    setTaxPayer(taxPayer);
    setShowTaxPayerEditModal(true);
  };

  const onSpecialPermissionsSuccess = (
    permissions: TaxPayerSpecialPermissions,
  ) => {
    setItems((previous) =>
      previous.map((item) =>
        item.id === taxPayerForPermissions?.id
          ? { ...item, ...permissions }
          : item,
      ),
    );
    setTaxPayerForPermissions(undefined);
    successAlert("Permisos especiales guardados con éxito!");
  };

  const onSuspensionSuccess = (suspended: boolean) => {
    setTaxPayerForSuspension(undefined);
    successAlert(
      suspended
        ? "Contribuyente suspendido con éxito!"
        : "Contribuyente reactivado con éxito!",
    );
    setItems([]);
    setLoading(true);
    fetchItems();
  };

  const onEditCancel = () => {
    setShowTaxPayerEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowTaxPayerEditModal(false);
    setItems([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchItems();
  };

  const onUpdateLimitsClick = (taxPayer: TaxPayer) => {
    setTaxPayerForLimits(taxPayer);
    setShowTaxPayerLimitsModal(true);
  };

  const onLimitsEditCancel = () => {
    setShowTaxPayerLimitsModal(false);
  };

  const onLimitsEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowTaxPayerLimitsModal(false);
    setItems([]);
    setLoading(true);
    fetchItems();
  };

  const onUpdateFixedLimitsClick = (taxPayer: TaxPayer) => {
    setTaxPayerForFixedLimits(taxPayer);
    setShowTaxPayerFixedLimitsModal(true);
  };

  const onFixedLimitsEditCancel = () => {
    setShowTaxPayerFixedLimitsModal(false);
  };

  const onFixedLimitsEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowTaxPayerFixedLimitsModal(false);
    setItems([]);
    setLoading(true);
    fetchItems();
  };

  const onUpdateExtraGrantsClick = (taxPayer: TaxPayer) => {
    setTaxPayerForExtraGrants(taxPayer);
    setShowTaxPayerExtraGrantsModal(true);
  };

  const onExtraGrantsCancel = () => {
    setShowTaxPayerExtraGrantsModal(false);
  };

  const onExtraGrantsSuccess = () => {
    // Form keeps modal open so multiple grants can be added.
  };

  const onBackupClick = async (taxPayer: TaxPayer) => {
    setBackingUpId(taxPayer.id!);
    const result = await backupTaxPayer(taxPayer.id!);
    setBackingUpId(null);
    if (result.status === SUCCESS && result.blob) {
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_taxpayer_${taxPayer.id}_${taxPayer.ruc}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      warningAlert(result.detail ?? "Error al realizar el backup");
    }
  };

  const onRestoreClick = () => {
    restoreInputRef.current?.click();
  };

  const onRestoreFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be selected again if needed
    e.target.value = "";
    setRestoring(true);
    const result = await restoreTaxPayer(file);
    setRestoring(false);
    if (result.status === SUCCESS) {
      const tp = result.data?.taxPayer;
      successAlert(
        `Contribuyente restaurado con éxito! RUC: ${tp?.ruc} — ID: ${tp?.id}`,
      );
      setItems([]);
      setLoading(true);
      fetchItems();
    } else {
      warningAlert(result.detail ?? "Error al restaurar el contribuyente");
    }
  };

  useEffect(() => {
    setSearch(getURLParams().get("search"));
    setIsDemoFilter(getURLParams().get("is_demo"));
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("taxPayers")) {
        return;
      }
      fetchItems();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Contribuyentes</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                </CButton>
              </div>
              <div className="p-2">
                <CTooltip content="Restaurar contribuyente desde backup">
                  <CButton
                    color="success"
                    className="float-right"
                    onClick={onRestoreClick}
                    disabled={restoring}
                  >
                    {restoring ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                    ) : (
                      <i className="fa fa-upload" />
                    )}
                  </CButton>
                </CTooltip>
                <input
                  ref={restoreInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  style={{ display: "none" }}
                  onChange={onRestoreFileChange}
                />
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow className=" mt-2">
                <CCol md={1}>
                  <CLabel className={"mt-2"}>Tipo:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CSelect value={isDemoFilter} onChange={onIsDemoFilterChange}>
                    <option value="">Todos</option>
                    <option value="false">Producción</option>
                    <option value="true">Demo</option>
                  </CSelect>
                </CCol>
              </CRow>

              <CRow className="mb-4">
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Buscar por RUC, razón social o nombre de fantasía"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Filtrar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={items}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    socialName: (item: TaxPayer) => {
                      return (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          <div>{item.socialName || "-"}</div>
                          {item.fantasyName && (
                            <small className="text-muted">
                              {item.fantasyName}
                            </small>
                          )}
                        </td>
                      );
                    },
                    usage: (item: TaxPayer) => {
                      if (item.usage === undefined) {
                        return (
                          <td
                            className="text-center"
                            style={{ verticalAlign: "middle" }}
                          >
                            <span className="text-muted">Sin límite</span>
                          </td>
                        );
                      }

                      const receipts = item.usage.receipts;

                      return (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {renderUsageBar(
                            item.usage.des,
                            receipts ? "DE" : undefined,
                          )}
                          {receipts && renderUsageBar(receipts, "REC", true)}
                        </td>
                      );
                    },
                    type: (item: TaxPayer) => {
                      return (
                        <td className="text-center">
                          {item.type
                            ? (TAX_PAYER_TYPE_CHOICES.get(item.type) ??
                              item.type)
                            : "-"}
                        </td>
                      );
                    },
                    isDemo: (item: TaxPayer) => {
                      return (
                        <td className="text-center">
                          <CBadge color={item.isDemo ? "warning" : "success"}>
                            {item.isDemo ? "Demo" : "Producción"}
                          </CBadge>
                        </td>
                      );
                    },
                    suspension: (item: TaxPayer) => {
                      return (
                        <td className="text-center">
                          {item.suspension ? (
                            <CTooltip
                              content={`${item.suspension.reasonName} desde el ${new Date(
                                item.suspension.suspendedAt,
                              ).toLocaleDateString()}`}
                            >
                              <CBadge color="danger">Suspendido</CBadge>
                            </CTooltip>
                          ) : (
                            <CBadge color="success">Activo</CBadge>
                          )}
                        </td>
                      );
                    },
                    createdAt: (item: TaxPayer) => {
                      return (
                        <td className="text-center">
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleDateString()
                            : "-"}
                        </td>
                      );
                    },
                    actions: (item: TaxPayer) => {
                      const limitsButton = (
                        <CTooltip content="Límites">
                          <CButton
                            className="text-white"
                            color="secondary"
                            onClick={() => {
                              onUpdateLimitsClick(item);
                            }}
                          >
                            <i className="fa fa-hand"></i>
                          </CButton>
                        </CTooltip>
                      );
                      const fixedLimitsButton = (
                        <CTooltip content="Límites Fijos">
                          <CButton
                            className="text-white"
                            color="primary"
                            onClick={() => {
                              onUpdateFixedLimitsClick(item);
                            }}
                          >
                            <i className="fa fa-lock"></i>
                          </CButton>
                        </CTooltip>
                      );
                      const extraGrantsButton = (
                        <CTooltip content="Concesiones Extras">
                          <CButton
                            className="text-white"
                            color="success"
                            onClick={() => {
                              onUpdateExtraGrantsClick(item);
                            }}
                          >
                            <i className="fa fa-plus"></i>
                          </CButton>
                        </CTooltip>
                      );
                      const usersButton = (
                        <CTooltip content="Usuarios">
                          <Link
                            className="btn btn-info"
                            to={generatePath(getRoutePath("userTaxPayers"), {
                              id: item.id,
                            })}
                          >
                            <i className="fa fa-user"></i>
                          </Link>
                        </CTooltip>
                      );
                      const editButton = (
                        <CTooltip content="Editar">
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </CTooltip>
                      );
                      const specialPermissionsButton = (
                        <CTooltip content="Permisos especiales">
                          <CButton
                            color="secondary"
                            aria-label={`Permisos especiales de ${item.socialName || item.ruc}`}
                            onClick={() => setTaxPayerForPermissions(item)}
                          >
                            <i
                              className="fa-solid fa-shield-halved"
                              aria-hidden="true"
                            />
                          </CButton>
                        </CTooltip>
                      );
                      const suspensionButton = (
                        <CTooltip
                          content={
                            item.suspension ? "Reactivar" : "Suspender"
                          }
                        >
                          <CButton
                            color={item.suspension ? "success" : "danger"}
                            aria-label={`${item.suspension ? "Reactivar" : "Suspender"} a ${item.socialName || item.ruc}`}
                            onClick={() => setTaxPayerForSuspension(item)}
                          >
                            <i
                              className={
                                item.suspension ? "fa fa-play" : "fa fa-ban"
                              }
                              aria-hidden="true"
                            />
                          </CButton>
                        </CTooltip>
                      );
                      const backupButton = (
                        <CTooltip content="Backup">
                          <CButton
                            className="text-white"
                            color="dark"
                            onClick={() => onBackupClick(item)}
                            disabled={backingUpId === item.id}
                          >
                            {backingUpId === item.id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              />
                            ) : (
                              <i className="fa fa-download" />
                            )}
                          </CButton>
                        </CTooltip>
                      );

                      return (
                        <td className="text-center">
                          <CButtonGroup>
                            {limitsButton}
                            {fixedLimitsButton}
                            {extraGrantsButton}
                            {usersButton}
                            {specialPermissionsButton}
                            {editButton}
                            {suspensionButton}
                            {backupButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <TaxPayerUpdateModal
        show={showTaxPayerEditModal}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
        taxPayer={taxPayer}
      />
      <TaxPayerSpecialPermissionsModal
        show={taxPayerForPermissions !== undefined}
        taxPayer={taxPayerForPermissions}
        onCancel={() => setTaxPayerForPermissions(undefined)}
        onSuccess={onSpecialPermissionsSuccess}
      />
      <TaxPayerSuspensionModal
        show={taxPayerForSuspension !== undefined}
        taxPayer={taxPayerForSuspension}
        onCancel={() => setTaxPayerForSuspension(undefined)}
        onSuccess={onSuspensionSuccess}
      />
      <TaxPayerLimitsModal
        show={showTaxPayerLimitsModal}
        onCancel={onLimitsEditCancel}
        onSuccess={onLimitsEditSuccess}
        taxPayer={taxPayerForLimits}
      />
      <TaxPayerFixedLimitsModal
        show={showTaxPayerFixedLimitsModal}
        onCancel={onFixedLimitsEditCancel}
        onSuccess={onFixedLimitsEditSuccess}
        taxPayer={taxPayerForFixedLimits}
      />
      <TaxPayerExtraGrantsModal
        show={showTaxPayerExtraGrantsModal}
        onCancel={onExtraGrantsCancel}
        onSuccess={onExtraGrantsSuccess}
        taxPayer={taxPayerForExtraGrants}
      />
    </>
  );
};

export default TaxPayerCrud;
